import { motion, useReducedMotion } from "framer-motion";
import { MaskReveal, Rise } from "@/components/site/Reveal";
import { AmazonButton } from "@/components/site/CTA";
import { ASSETS, AMAZON_WWDSS } from "@/lib/site";

export const Hero = () => {
  const reduce = useReducedMotion();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink flex items-center pt-28 pb-16"
    >
      {/* atmospheric wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 right-[-10%] h-[80vh] w-[80vh] rounded-full bg-[radial-gradient(circle,rgba(60,60,60,0.35),transparent_60%)] blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[40vh] w-full bg-gradient-to-t from-ink-pure to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 items-center">
          {/* Left — typography */}
          <motion.div className="lg:col-span-7 order-2 lg:order-1">
            <Rise delay={0.1}>
              <p className="font-sans text-[0.7rem] sm:text-xs uppercase tracking-[0.42em] text-rust/90 mb-6">
                New from Grace Edwards
              </p>
            </Rise>

            <MaskReveal
              as="h1"
              lines={["WOMEN WHO DON’T", "SIT STILL"]}
              delay={0.15}
              className="font-serif uppercase leading-[0.9] tracking-tight text-bone/90 text-4xl sm:text-6xl lg:text-7xl xl:text-[6.5rem]"
              testId="hero-title"
            />

            <Rise delay={0.5} className="mt-8 max-w-xl">
              <p className="font-serif italic text-xl sm:text-2xl text-bone-soft">
                Freedom, Motorcycles, and the Women Who Refuse to Stay Small
              </p>
            </Rise>

            <Rise delay={0.65} className="mt-10 max-w-lg border-l border-bone/20 pl-6">
              <p className="font-serif text-2xl sm:text-3xl leading-snug text-bone">
                This was never about the motorcycle.
              </p>
              <p className="mt-4 font-sans text-sm sm:text-base leading-relaxed text-bone-muted">
                It’s about what happens when you stop settling, stop shrinking
                and stop waiting for the right time.
              </p>
            </Rise>

            <Rise delay={0.8} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
              <AmazonButton href={AMAZON_WWDSS} testId="hero-buy-button">
                Buy on Amazon
              </AmazonButton>
              <div className="font-sans text-[0.68rem] uppercase tracking-[0.28em] text-bone-muted leading-relaxed">
                By Grace Edwards
                <span className="block text-bone-muted/60 normal-case tracking-normal font-serif italic text-sm mt-1">
                  Author of Ride Like a Lady
                </span>
              </div>
            </Rise>
          </motion.div>

          {/* Right — book cover */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
            <motion.div
              style={{ perspective: 1400 }}
              initial={reduce ? { opacity: 1 } : { opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="relative"
            >
              <motion.img
                src={ASSETS.wwdssCover}
                alt="Book cover of Women Who Don’t Sit Still by Grace Edwards"
                width={520}
                fetchPriority="high"
                className="cover-shadow w-[76vw] max-w-[370px] sm:max-w-[430px] lg:max-w-[520px] h-auto"
                style={{ transform: "rotateY(-9deg) rotateX(2deg)" }}
                data-testid="hero-book-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        <div className="h-12 w-px bg-gradient-to-b from-bone/50 to-transparent" />
      </motion.div>
    </section>
  );
};
